/**
 * Le poche regole di testo dell'eliminazione definitiva, tenute fuori da React.
 *
 * Stanno qui e non dentro `DialogoEliminazione.tsx` per un motivo solo: il
 * confronto del nome ricopiato è **l'unica cosa che separa un clic distratto da
 * una cancellazione irreversibile**, e va provata. Una funzione pura si prova
 * con `node --test`, un campo dentro a un componente no.
 */
import type {
  CollegateEliminazione,
  EsitoEliminazione,
  ImpattoEliminazione,
  SceltaCollegate,
  VoceImpatto,
} from '../api/tipi-dati.ts';

/**
 * Il testo digitato, ridotto alla forma in cui va confrontato.
 *
 * Si tolgono **solo** gli spazi davanti e dietro — quelli che si portano dietro
 * un copia-incolla o un doppio spazio finale — e si ignorano maiuscole e
 * minuscole. Gli spazi *interni* restano significativi: «Mario Rossi» e
 * «MarioRossi» sono due cose diverse, e chi sta cancellando deve aver letto il
 * nome, non averlo intuito.
 *
 * `toLocaleLowerCase('it')` e non `toLowerCase()`: sono equivalenti sulle
 * lettere italiane, ma dichiarare la lingua evita la sorpresa turca sulla «I»
 * il giorno in cui in anagrafica finisse un nome straniero.
 */
export function normalizzaNome(testo: string): string {
  return testo.trim().toLocaleLowerCase('it');
}

/**
 * Il nome ricopiato combacia con quello dell'elemento?
 *
 * ⚠️ Un nome atteso vuoto non combacia **mai**, nemmeno con un campo vuoto: se
 * combaciasse, un elemento senza nome sbloccherebbe il pulsante rosso da solo,
 * senza che nessuno abbia digitato niente. È il caso che non deve poter
 * succedere, ed è il motivo per cui questa funzione esiste invece di un
 * `===` scritto sul posto.
 */
export function nomeCombacia(digitato: string, atteso: string): boolean {
  const riferimento = normalizzaNome(atteso);
  if (riferimento === '') return false;
  return normalizzaNome(digitato) === riferimento;
}

// -----------------------------------------------------------------------------
// La seconda domanda: che fine fanno le scadenze collegate
// -----------------------------------------------------------------------------

/** Le entità per cui la seconda domanda ha senso. Le altre non la vedono mai. */
export type EntitaConCollegate = 'soggetto' | 'tipo';

/**
 * Va posta la seconda domanda?
 *
 * Tre condizioni, e servono tutte e tre:
 *
 * 1. l'entità è un soggetto o un tipo — per scadenze e utenti il contratto non
 *    prevede nessuna scelta, e la loro `DELETE` non accetta corpo. Questo
 *    controllo è ridondante rispetto al secondo (il backend manda `collegate:
 *    null` per quelle due), ed è **voluto**: se un giorno il backend mandasse
 *    la scelta dove non serve, qui si porrebbe una domanda la cui risposta non
 *    avrebbe dove andare;
 * 2. c'è davvero una scelta da fare;
 * 3. c'è almeno una scadenza collegata — chiedere «che ne faccio di zero
 *    scadenze?» è una domanda senza risposta giusta.
 *
 * Non dipende da `eliminabile`: quello lo controlla il dialogo prima ancora di
 * arrivare qui, perché quando è falso non c'è nessun passo, né primo né secondo.
 */
export function serveSceltaCollegate(
  entita: string,
  impatto: Pick<ImpattoEliminazione, 'collegate'>,
): boolean {
  if (entita !== 'soggetto' && entita !== 'tipo') return false;
  if (impatto.collegate === null) return false;
  return impatto.collegate.quante > 0;
}

/**
 * Il pulsante rosso finale si può premere?
 *
 * ⚠️ È la guardia che tiene insieme i due passi, e il motivo per cui sta qui
 * invece che dentro un `disabled` scritto sul posto. Sono due condizioni
 * indipendenti e **nessuna delle due basta da sola**: il nome ricopiato dice
 * *quale* elemento si sta cancellando, la scelta dice *quanto in là* arriva la
 * cancellazione. Un dialogo che si accontentasse della prima eseguirebbe un
 * ramo che nessuno ha scelto.
 */
export function puoConfermare({
  nomeRicopiato,
  serveScelta,
  scelta,
}: {
  /** Il confronto già fatto: `nomeCombacia(digitato, atteso)`. */
  nomeRicopiato: boolean;
  serveScelta: boolean;
  scelta: SceltaCollegate | null;
}): boolean {
  if (!nomeRicopiato) return false;
  if (serveScelta && scelta === null) return false;
  return true;
}

/**
 * Cosa mandare come `collegate` nella `DELETE`.
 *
 * `undefined` quando non c'è niente da chiedere: il contratto dice che in quel
 * caso il campo è superfluo, e mandarlo lo stesso significherebbe affermare una
 * scelta che nessuno ha fatto.
 *
 * ⚠️ Torna `undefined` **anche** se la scelta manca pur essendo richiesta.
 * Sembra un caso impossibile — il pulsante è disabilitato — ma se lo fosse
 * davvero non ci sarebbe bisogno di deciderlo qui: se un giorno quel pulsante
 * partisse comunque, il backend risponderebbe 400 invece di eseguire il ramo
 * che gli pare. Non inventarsi un `'SCOLLEGA'` di riserva è il punto: sarebbe
 * comodo, e sarebbe una scelta presa al posto dell'utente.
 */
export function collegateDaMandare(
  serveScelta: boolean,
  scelta: SceltaCollegate | null,
): SceltaCollegate | undefined {
  if (!serveScelta) return undefined;
  if (scelta === null) return undefined;
  return scelta;
}

/** «12 scadenze», «1 scadenza». */
function contaScadenze(quante: number): string {
  return quante === 1 ? '1 scadenza' : `${quante} scadenze`;
}

/**
 * Uno dei due destini possibili, già scritto in italiano.
 *
 * ⚠️ Non è un sì e un no, ed è tutto il punto di questa struttura: **due
 * destini con lo stesso peso grafico**, ciascuno col proprio titolo che dice
 * cosa succede, non «Sì»/«No» accanto a una domanda. Un sì e un no sembrano
 * intercambiabili; questi due no.
 */
export interface DestinoCollegate {
  valore: SceltaCollegate;
  /** L'etichetta della scelta: dice cosa succede, non «sì» o «no». */
  titolo: string;
  /** Cosa comporta davvero, in una frase. */
  effetto: string;
  /**
   * Quello che va letto prima di scegliere questo ramo, o `null`.
   * Valorizzato **solo** dove serve: se comparisse su tutti e due i destini
   * smetterebbe di distinguerli.
   */
  avvertimento: string | null;
}

/**
 * I destini da presentare, nell'ordine in cui vanno mostrati.
 *
 * **Il conservativo per primo, e non è indifferente**: è quello su cui cade
 * l'occhio, ed è quello che si sceglie per sbaglio se si sceglie per sbaglio.
 * Vale la stessa regola per cui il backend descrive `SCOLLEGA` quando non gli
 * si chiede niente — il ramo che distrugge di più non è mai quello che capita.
 *
 * Nessuno dei due è preselezionato: quello lo decide il dialogo, e la ragione è
 * la stessa. Qui si decide solo l'ordine e le parole.
 *
 * Si rispetta `collegate.scelte`: se un giorno il backend ne offrisse una sola,
 * mostrarne due sarebbe promettere qualcosa che verrebbe poi rifiutato.
 */
export function destiniCollegate({
  collegate,
  entita,
  nome,
}: {
  collegate: CollegateEliminazione;
  entita: EntitaConCollegate;
  /** Il nome che resterà congelato sulle righe: va citato, non riassunto. */
  nome: string;
}): DestinoCollegate[] {
  const quante = contaScadenze(collegate.quante);
  const singolare = collegate.quante === 1;

  // «continueranno a dire di chi erano» per un soggetto, «che cosa erano» per
  // un tipo: è la frase che spiega perché una riga orfana resta leggibile.
  const diCosa = entita === 'soggetto' ? 'di chi' : 'che cosa';
  const scheda = entita === 'soggetto' ? 'una scheda' : 'un tipo';

  const conserva: DestinoCollegate = {
    valore: 'SCOLLEGA',
    titolo: singolare ? 'Conserva la scadenza' : `Conserva le ${quante}`,
    effetto:
      `Rest${singolare ? 'a' : 'ano'} nello storico, ma orfan${singolare ? 'a' : 'e'}: ` +
      `continuer${singolare ? 'à' : 'anno'} a dire ${diCosa} ` +
      `era${singolare ? '' : 'no'} — «${nome}» resta scritto sulla riga — senza però ` +
      `essere più collegat${singolare ? 'a' : 'e'} a ${scheda} che esiste.`,
    avvertimento: null,
  };

  const elimina: DestinoCollegate = {
    valore: 'ELIMINA',
    titolo: singolare
      ? 'Elimina anche la scadenza collegata'
      : `Elimina anche le ${quante} collegate`,
    effetto:
      `Spariscono loro, i loro allegati e le notifiche già inviate. ` +
      `Della prova che quelle verifiche erano state fatte non resta niente.`,
    avvertimento:
      (collegate.aperte > 0
        ? collegate.aperte === 1
          ? 'Una è ancora aperta. '
          : `${collegate.aperte} sono ancora aperte. `
        : '') + 'Non si torna indietro: si recupera solo da un backup del database.',
  };

  return [conserva, elimina].filter((d) => collegate.scelte.includes(d.valore));
}

/**
 * La domanda, esattamente come va posta.
 *
 * È una domanda aperta — «che fine fanno?» — e non «vuoi eliminarle anche?».
 * Una domanda con dentro già un verbo è una domanda a cui si risponde sì o no,
 * e i due rami tornerebbero a sembrare uno la negazione dell'altro.
 */
export function domandaCollegate(collegate: CollegateEliminazione): string {
  return collegate.quante === 1
    ? 'Che fine fa la scadenza collegata?'
    : `Che fine fanno le ${collegate.quante} scadenze collegate?`;
}

/**
 * Cosa scrivere sul pulsante che cancella davvero.
 *
 * ⚠️ **Non «Conferma».** Il pulsante finale deve dire quale dei due rami sta
 * per partire, altrimenti i due destini tornano a sembrare intercambiabili
 * proprio nel punto in cui smettono di esserlo. È anche l'ultima occasione per
 * accorgersi di aver selezionato l'altra riga.
 */
export function etichettaConferma(
  collegate: CollegateEliminazione | null,
  scelta: SceltaCollegate | null,
): string {
  if (collegate === null || scelta === null) return 'Elimina definitivamente';

  const quante = contaScadenze(collegate.quante);
  return scelta === 'ELIMINA'
    ? `Elimina, insieme a ${quante}`
    : `Elimina, conservando ${quante}`;
}

/** «3 allegati, 12 notifiche e 1 sessione»: la "e" prima dell'ultimo. */
function elencaInItaliano(pezzi: string[]): string {
  if (pezzi.length <= 1) return pezzi[0] ?? '';
  return `${pezzi.slice(0, -1).join(', ')} e ${pezzi[pezzi.length - 1] ?? ''}`;
}

/** «3 allegati» — `cosa` arriva già al plurale e con la maiuscola dal backend. */
function conta(v: VoceImpatto): string {
  return `${v.quanti} ${v.cosa.toLocaleLowerCase('it')}`;
}

/**
 * La riga di conferma che compare in cima all'elenco dopo la cancellazione.
 *
 * Senza genere grammaticale — «non esiste più» vale per una scadenza, un
 * soggetto, un tipo e un utente — perché il messaggio è uno solo per tutte e
 * quattro le entità e «eliminato/eliminata» costringerebbe a passarsi dietro il
 * genere da ogni punto di chiamata solo per far tornare una desinenza.
 *
 * Ripete cosa si è portato via: chi ha appena confermato ha letto quei numeri
 * *prima*, ed è giusto che li ritrovi come ricevuta di quello che è successo
 * davvero, non di quello che era stato previsto.
 */
export function messaggioEliminato(esito: EsitoEliminazione): string {
  const spariti = esito.conseguenze.filter((c) => c.effetto === 'ELIMINATO').map(conta);
  const scollegati = esito.conseguenze.filter((c) => c.effetto === 'SCOLLEGATO').map(conta);

  const frasi = [`«${esito.oggetto}» non esiste più.`];
  if (spariti.length > 0) frasi.push(`Spariti anche ${elencaInItaliano(spariti)}.`);
  if (scollegati.length > 0) {
    frasi.push(`Restano senza collegamento ${elencaInItaliano(scollegati)}.`);
  }

  return frasi.join(' ');
}
